import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import {
  Badge,
  Button,
  Input,
  Label,
  ListGroup,
  ListGroupItem,
  Offcanvas,
  OffcanvasBody,
  OffcanvasHeader,
  Spinner,
} from "reactstrap";

import { canAccessOpenOrdersPanel } from "../../Routes/roleAccess";
import { formatMoneyFromCents } from "../../helpers/money";
import { getAll, Order } from "../../services/orderService";

const POLL_INTERVAL_MS = 30_000;
const LIST_PER_PAGE = 100;

const formatOpenSince = (
  createdAt: string,
  translate: (key: string, options?: Record<string, unknown>) => string
): string => {
  const diffMs = Date.now() - new Date(createdAt).getTime();
  const minutes = Math.floor(diffMs / 60_000);

  if (minutes < 1) {
    return translate("orders.offcanvas.openSince.justNow");
  }

  if (minutes < 60) {
    return translate("orders.offcanvas.openSince.minutes", { count: minutes });
  }

  const hours = Math.floor(minutes / 60);

  if (hours < 24) {
    return translate("orders.offcanvas.openSince.hours", { count: hours });
  }

  const days = Math.floor(hours / 24);

  return translate("orders.offcanvas.openSince.days", { count: days });
};

const OpenOrdersOffcanvas = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

  const [isOpen, setIsOpen] = useState(false);
  const [orders, setOrders] = useState<Order[]>([]);
  const [openCount, setOpenCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [courtesyOnly, setCourtesyOnly] = useState(false);

  const notAvailable = t("studio.orders.common.notAvailable");
  const panelAllowed = canAccessOpenOrdersPanel();

  const loadOpenOrders = useCallback(async () => {
    if (!panelAllowed) {
      return;
    }

    setLoading(true);

    try {
      const response = await getAll({
        status: "open",
        page: 1,
        per_page: LIST_PER_PAGE,
        ...(courtesyOnly ? { has_discount: 1 } : {}),
      });

      setOrders(response.data);
      setOpenCount(response.meta?.total ?? response.data.length);
    } catch {
      setOrders([]);
      setOpenCount(0);
    } finally {
      setLoading(false);
    }
  }, [courtesyOnly, panelAllowed]);

  useEffect(() => {
    if (!panelAllowed) {
      return;
    }

    loadOpenOrders();

    const intervalId = window.setInterval(loadOpenOrders, POLL_INTERVAL_MS);

    const handleVisibilityOrFocus = () => {
      if (document.visibilityState === "visible") {
        loadOpenOrders();
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityOrFocus);
    window.addEventListener("focus", handleVisibilityOrFocus);

    return () => {
      window.clearInterval(intervalId);
      document.removeEventListener("visibilitychange", handleVisibilityOrFocus);
      window.removeEventListener("focus", handleVisibilityOrFocus);
    };
  }, [loadOpenOrders, panelAllowed]);

  useEffect(() => {
    if (isOpen) {
      loadOpenOrders();
    }
  }, [isOpen, loadOpenOrders]);

  const toggleOffcanvas = () => {
    setIsOpen((previous) => !previous);
  };

  const handleSelectOrder = (orderId: number) => {
    setIsOpen(false);
    navigate(`/orders/${orderId}`);
  };

  const formatMoney = (cents: number) => formatMoneyFromCents(cents, i18n.language);

  if (!panelAllowed) {
    return null;
  }

  return (
    <>
      <div className="customizer-setting d-none d-md-block">
        <Button
          color="primary"
          className="btn-icon btn-lg p-2 rounded-pill shadow-lg position-relative"
          onClick={toggleOffcanvas}
          aria-label={t("orders.offcanvas.fabLabel")}
        >
          <i className="ri-file-list-3-line fs-22" aria-hidden="true" />
          {openCount > 0 && (
            <Badge
              color="danger"
              pill
              className="position-absolute top-0 start-100 translate-middle"
            >
              {openCount > 99 ? "99+" : openCount}
            </Badge>
          )}
        </Button>
      </div>

      <Offcanvas isOpen={isOpen} toggle={toggleOffcanvas} direction="end" className="border-0">
        <OffcanvasHeader toggle={toggleOffcanvas} className="border-bottom">
          {t("orders.offcanvas.title")}
        </OffcanvasHeader>
        <OffcanvasBody>
          <div className="form-check form-switch mb-3">
            <Input
              id="open-orders-courtesy-only"
              type="checkbox"
              role="switch"
              className="form-check-input"
              checked={courtesyOnly}
              onChange={(event) => setCourtesyOnly(event.target.checked)}
            />
            <Label className="form-check-label" htmlFor="open-orders-courtesy-only">
              {t("orders.offcanvas.courtesyOnly")}
            </Label>
          </div>
          {loading && orders.length === 0 ? (
            <div className="text-center py-4">
              <Spinner color="primary">{t("orders.offcanvas.loading")}</Spinner>
            </div>
          ) : orders.length === 0 ? (
            <p className="text-muted mb-0">
              {courtesyOnly
                ? t("orders.offcanvas.emptyCourtesy")
                : t("orders.offcanvas.empty")}
            </p>
          ) : (
            <ListGroup flush>
              {orders.map((order) => (
                <ListGroupItem
                  key={order.id}
                  tag="button"
                  type="button"
                  action
                  className="px-0 py-3 border-bottom"
                  onClick={() => handleSelectOrder(order.id)}
                >
                  <div className="d-flex justify-content-between align-items-start gap-2">
                    <div className="text-start">
                      <p className="fw-semibold mb-1">
                        {order.client?.name ?? notAvailable}
                        {(order.discount_total_cents ?? 0) > 0 ? (
                          <Badge color="soft-info" className="ms-2 fw-normal">
                            {t("studio.orders.discount.courtesyBadge")}
                          </Badge>
                        ) : null}
                      </p>
                      <p className="text-muted small mb-1">
                        {t("orders.offcanvas.unit")}: {order.unit?.name ?? notAvailable}
                      </p>
                      <p className="text-muted small mb-0">
                        {formatOpenSince(order.created_at, t)}
                      </p>
                    </div>
                    <div className="text-end">
                      <span className="text-muted small d-block mb-1">
                        {t("orders.offcanvas.partialTotal")}
                      </span>
                      <span className="fw-semibold">{formatMoney(order.total_amount)}</span>
                      {(order.discount_total_cents ?? 0) > 0 ? (
                        <span className="text-success small d-block">
                          {t("studio.orders.discount.listDiscount", {
                            amount: formatMoney(order.discount_total_cents ?? 0),
                          })}
                        </span>
                      ) : null}
                    </div>
                  </div>
                </ListGroupItem>
              ))}
            </ListGroup>
          )}
        </OffcanvasBody>
      </Offcanvas>
    </>
  );
};

export default OpenOrdersOffcanvas;
